/* ==========================================================================
   Pawly Supply — global.js
   Cart (AJAX + drawer), header behaviours, popup, wishlist, utilities.
   No dependencies. Runs on every page.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme || {};
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || T.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    const match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---------- Toast ---------- */
  function toast(message, type) {
    const region = $('#ToastRegion');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
    el.textContent = message;
    region.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity .3s'; }, 2600);
    setTimeout(() => el.remove(), 3000);
  }

  /* ---------- Body scroll lock ---------- */
  let lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('overflow-hidden', lockCount > 0);
  }

  /* ---------- Focus trap (light) ---------- */
  function trapFocus(container) {
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return () => {};
    const first = focusable[0], last = focusable[focusable.length - 1];
    function onKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    setTimeout(() => { const t = $('[data-autofocus]', container) || first; t.focus({ preventScroll: true }); }, 30);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ==========================================================================
     Cart
     ========================================================================== */
  const Cart = {
    drawer: null,
    _untrap: null,

    init() {
      this.drawer = $('#CartDrawer');
      document.addEventListener('click', (e) => {
        const open = e.target.closest('[data-cart-open]');
        if (open) { e.preventDefault(); if (T.cartType === 'drawer' && this.drawer) this.open(); else window.location.href = T.routes.cart; }
        const close = e.target.closest('[data-cart-close]');
        if (close) { e.preventDefault(); this.close(); }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.change(remove.dataset.cartRemove, 0, remove.closest('.cart-item')); }
        const recAdd = e.target.closest('[data-cart-rec-add]');
        if (recAdd) { e.preventDefault(); this.add({ id: recAdd.dataset.cartRecAdd, quantity: 1 }, recAdd); }
      });
      document.addEventListener('change', (e) => {
        const qty = e.target.closest('[data-cart-qty]');
        if (qty) this.change(qty.dataset.cartQty, parseInt(qty.value, 10) || 0, qty.closest('.cart-item'));
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      // Note field on cart page
      const note = $('[data-cart-note]');
      if (note) {
        let t;
        note.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => fetch(T.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) }), 500);
        });
      }
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      this._untrap = trapFocus($('.drawer', this.drawer));
    },

    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this._untrap) this._untrap();
    },

    setLoading(btn, on) {
      if (!btn) return;
      btn.classList.toggle('is-loading', on);
      btn.disabled = on;
    },

    async add(payload, btn) {
      this.setLoading(btn, true);
      try {
        const body = Object.assign({ sections: this.sectionsToRender(), sections_url: window.location.pathname }, payload);
        const res = await fetch(T.routes.cartAdd + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || T.strings.cartError);
        await this.refresh(data.sections);
        if (btn) {
          const label = btn.querySelector('.btn__label') || btn;
          const original = label.textContent;
          btn.classList.add('is-added');
          label.textContent = T.strings.added;
          setTimeout(() => { btn.classList.remove('is-added'); label.textContent = original; }, 1500);
        }
        if (T.cartType === 'drawer' && this.drawer) this.open(); else toast(T.strings.added);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (err) {
        toast(err.message || T.strings.cartError, 'error');
      } finally {
        this.setLoading(btn, false);
      }
    },

    async change(line, quantity, itemEl) {
      if (itemEl) itemEl.classList.add('is-loading');
      try {
        const res = await fetch(T.routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ line: line, quantity: quantity, sections: this.sectionsToRender(), sections_url: window.location.pathname }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || T.strings.cartError);
        await this.refresh(data.sections);
      } catch (err) {
        toast(err.message || T.strings.cartError, 'error');
        if (itemEl) itemEl.classList.remove('is-loading');
      }
    },

    sectionsToRender() {
      const ids = [];
      if (this.drawer) ids.push('cart-drawer');
      const page = $('[data-cart-page-section]');
      if (page) ids.push(page.dataset.cartPageSection);
      return ids;
    },

    async refresh(sections) {
      if (!sections) {
        const ids = this.sectionsToRender();
        if (!ids.length) return this.updateCount();
        const res = await fetch(window.location.pathname + '?sections=' + ids.join(','));
        sections = await res.json();
      }
      if (sections['cart-drawer'] && this.drawer) {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = $('#CartDrawer', html);
        if (fresh) {
          const wasOpen = this.drawer.classList.contains('is-open');
          this.drawer.innerHTML = fresh.innerHTML;
          if (wasOpen) this._untrap = trapFocus($('.drawer', this.drawer));
        }
      }
      const page = $('[data-cart-page-section]');
      if (page && sections[page.dataset.cartPageSection]) {
        const html = new DOMParser().parseFromString(sections[page.dataset.cartPageSection], 'text/html');
        const fresh = $('[data-cart-page-section]', html);
        if (fresh) page.innerHTML = fresh.innerHTML;
      }
      this.updateCount();
    },

    async updateCount() {
      try {
        const res = await fetch(T.routes.cart + '.js');
        const cart = await res.json();
        $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (e) { /* noop */ }
    }
  };

  /* Product forms (product page + card "Add to Cart") */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const fd = new FormData(form);
    const payload = { id: fd.get('id'), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
    const props = {};
    fd.forEach((v, k) => { const m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; });
    if (Object.keys(props).length) payload.properties = props;
    if (fd.get('selling_plan')) payload.selling_plan = fd.get('selling_plan');
    Cart.add(payload, btn);
  });

  /* ==========================================================================
     Quantity steppers (generic)
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrap = btn.closest('.qty');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '0', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let val = parseInt(input.value, 10) || 0;
    val += btn.dataset.qtyChange === 'inc' ? 1 : -1;
    val = Math.min(max, Math.max(min, val));
    if (val !== parseInt(input.value, 10)) {
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ==========================================================================
     Header
     ========================================================================== */
  const Header = {
    init() {
      this.initAnnouncement();
      this.initMega();
      this.initMobileNav();
      this.initSearch();
      this.initLocalization();
      this.initFooterAccordion();
    },

    initAnnouncement() {
      const items = $$('.announcement__mobile .announcement__item');
      if (items.length < 2) return;
      let i = 0;
      const interval = parseInt(($('.announcement__mobile') || {}).dataset?.interval || '3500', 10);
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, interval);
    },

    initMega() {
      const nav = $('[data-nav]');
      if (!nav) return;
      const items = $$('.nav__item--mega', nav);
      const closeAll = () => items.forEach((it) => it.classList.remove('is-open'));
      let timer;
      items.forEach((item) => {
        const link = $('.nav__link', item);
        item.addEventListener('mouseenter', () => { clearTimeout(timer); closeAll(); item.classList.add('is-open'); Search.close(); });
        link.addEventListener('focus', () => { closeAll(); item.classList.add('is-open'); });
        link.addEventListener('click', (e) => {
          // On touch devices first tap opens the mega menu, second navigates
          if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) { e.preventDefault(); closeAll(); item.classList.add('is-open'); }
        });
      });
      nav.addEventListener('mouseleave', () => { timer = setTimeout(closeAll, 120); });
      nav.addEventListener('focusout', (e) => { if (!nav.contains(e.relatedTarget)) closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
      document.addEventListener('click', (e) => { if (!nav.contains(e.target)) closeAll(); });
    },

    initMobileNav() {
      const drawer = $('#MobileNav');
      if (!drawer) return;
      let untrap = null;
      const open = () => { drawer.classList.remove('hidden'); drawer.setAttribute('aria-hidden', 'false'); lockScroll(true); untrap = trapFocus($('.drawer', drawer)); };
      const close = () => { if (drawer.classList.contains('hidden')) return; drawer.classList.add('hidden'); drawer.setAttribute('aria-hidden', 'true'); lockScroll(false); if (untrap) untrap(); };
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-mobile-nav-open]')) { e.preventDefault(); open(); }
        if (e.target.closest('[data-mobile-nav-close]')) { e.preventDefault(); close(); }
        const toggle = e.target.closest('[data-mobile-nav-toggle]');
        if (toggle) { e.preventDefault(); const li = toggle.closest('.mobile-nav__item'); const isOpen = li.classList.toggle('is-open'); toggle.setAttribute('aria-expanded', isOpen); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    },

    initLocalization() {
      $$('[data-localization]').forEach((wrap) => {
        const btn = $('[data-localization-toggle]', wrap);
        const form = $('form', wrap);
        if (!btn) return;
        btn.addEventListener('click', (e) => { e.preventDefault(); wrap.classList.toggle('is-open'); btn.setAttribute('aria-expanded', wrap.classList.contains('is-open')); });
        $$('select', wrap).forEach((sel) => sel.addEventListener('change', () => {
          const hidden = $('input[name="' + sel.dataset.name + '"]', form);
          if (hidden) hidden.value = sel.value;
          form.submit();
        }));
        document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) wrap.classList.remove('is-open'); });
      });
    },

    initFooterAccordion() {
      $$('[data-footer-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (window.innerWidth >= 760) return;
          const col = btn.closest('.footer__col');
          const open = col.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', open);
          const icon = $('[data-footer-icon]', btn);
          if (icon) icon.innerHTML = open ? icon.dataset.minus : icon.dataset.plus;
        });
      });
    },

    initSearch() { Search.init(); }
  };

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  const Search = {
    wraps: [],
    overlay: null,
    timer: null,
    controller: null,

    init() {
      this.wraps = $$('[data-search]');
      if (!this.wraps.length) return;
      this.overlay = $('#SearchOverlay');
      this.wraps.forEach((wrap) => {
        const input = $('input[type="search"]', wrap);
        const panel = $('[data-search-panel]', wrap);
        if (!input || !panel) return;
        input.addEventListener('focus', () => this.open(wrap));
        input.addEventListener('input', () => {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.fetch(wrap, input.value.trim()), 250);
        });
        input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.close(); input.blur(); } });
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    },

    open(wrap) {
      this.wraps.forEach((w) => w.classList.toggle('is-open', w === wrap));
      if (this.overlay) this.overlay.classList.remove('hidden');
    },

    close() {
      this.wraps.forEach((w) => w.classList.remove('is-open'));
      if (this.overlay) this.overlay.classList.add('hidden');
    },

    async fetch(wrap, q) {
      const results = $('[data-search-results]', wrap);
      const defaults = $('[data-search-defaults]', wrap);
      const more = $('[data-search-more]', wrap);
      if (!results) return;
      if (!q) {
        results.innerHTML = '';
        results.classList.add('hidden');
        if (defaults) defaults.classList.remove('hidden');
        if (more) more.href = T.routes.search;
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = T.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,article&resources[limit]=4&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][prefix]=last&section_id=predictive-search';
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('#PredictiveSearchResults', doc);
        results.innerHTML = inner ? inner.innerHTML : '';
        results.classList.remove('hidden');
        if (defaults) defaults.classList.add('hidden');
        if (more) more.href = T.routes.search + '?q=' + encodeURIComponent(q);
      } catch (err) { if (err.name !== 'AbortError') { /* silently ignore */ } }
    }
  };

  /* ==========================================================================
     Popup (first visit)
     ========================================================================== */
  const Popup = {
    init() {
      const popup = $('#Popup');
      if (!popup) return;
      const key = 'pawly_popup_seen_' + (popup.dataset.version || '1');
      const doneKey = 'pawly_welcome_done';
      const store = popup.dataset.frequency === 'always' ? sessionStorage : localStorage;
      const inEditor = window.Shopify && window.Shopify.designMode;
      const tab = $('[data-popup-open]');
      let done = false;
      try { done = !!localStorage.getItem(doneKey); } catch (e) {}
      const showTab = () => { if (tab && !done) tab.hidden = false; };
      const hideTab = () => { if (tab) tab.hidden = true; };
      let untrap = null;
      const open = () => { popup.classList.add('is-open'); popup.setAttribute('aria-hidden', 'false'); lockScroll(true); untrap = trapFocus($('.popup__dialog', popup)); hideTab(); };
      const close = () => { if (!popup.classList.contains('is-open')) return; popup.classList.remove('is-open'); popup.setAttribute('aria-hidden', 'true'); lockScroll(false); if (untrap) untrap(); try { store.setItem(key, '1'); } catch (e) {} showTab(); };
      popup.addEventListener('click', (e) => { if (e.target.closest('[data-popup-close]')) { e.preventDefault(); close(); } });
      if (tab) tab.addEventListener('click', open);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      let seen = false;
      try { seen = !!store.getItem(key); } catch (e) {}
      if (inEditor) {
        showTab();
        document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(popup)) open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(popup)) { popup.classList.remove('is-open'); lockScroll(false); showTab(); } });
        return;
      }
      const successful = popup.querySelector('.form-status--success');
      if (successful) {
        // Signed up: show the code, and retire the tab for this visitor
        done = true; hideTab();
        try { store.setItem(key, '1'); localStorage.setItem(doneKey, '1'); } catch (e) {}
        open();
        return;
      }
      if (!seen) setTimeout(open, parseInt(popup.dataset.delay || '1400', 10)); else showTab();
    }
  };

  /* ---------- Copy-to-clipboard (discount code) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    const text = btn.dataset.copy;
    const done = () => { const o = btn.textContent; btn.textContent = 'Copied ✓'; setTimeout(() => { btn.textContent = o; }, 1800); };
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(done).catch(done);
    else { const i = document.createElement('input'); i.value = text; document.body.appendChild(i); i.select(); try { document.execCommand('copy'); } catch (err) {} i.remove(); done(); }
  });

  /* ==========================================================================
     Wishlist (localStorage)
     ========================================================================== */
  const Wishlist = {
    key: 'pawly_wishlist',
    get() { try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; } },
    set(list) { try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) {} this.sync(); document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: list })); },
    has(handle) { return this.get().includes(handle); },
    toggle(handle) { const list = this.get(); const i = list.indexOf(handle); if (i > -1) list.splice(i, 1); else list.push(handle); this.set(list); return i === -1; },
    remove(handle) { this.set(this.get().filter((h) => h !== handle)); },
    sync() {
      // Idempotent: only touch the DOM when a value actually changes, so the
      // MutationObserver below can never re-trigger itself.
      const list = this.get();
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const on = String(list.includes(btn.dataset.wishlistToggle));
        if (btn.getAttribute('aria-pressed') !== on) {
          btn.setAttribute('aria-pressed', on);
          btn.setAttribute('aria-label', on === 'true' ? T.strings.wishlistRemove : T.strings.wishlistAdd);
        }
      });
      const count = String(list.length);
      $$('[data-wishlist-count]').forEach((el) => { if (el.textContent !== count) { el.textContent = count; el.dataset.count = count; } });
    },
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-toggle]');
        if (!btn) return;
        e.preventDefault();
        const added = this.toggle(btn.dataset.wishlistToggle);
        toast(added ? 'Saved to wishlist' : 'Removed from wishlist');
      });
      this.sync();
      // Re-sync when new cards are injected (load more / search / cart drawer). Debounced.
      let pending = null;
      new MutationObserver(() => {
        if (pending) return;
        pending = setTimeout(() => { pending = null; this.sync(); }, 50);
      }).observe(document.body, { childList: true, subtree: true });
    }
  };

  /* ---------- Product card variations (swap image / price / links) ---------- */
  const CardOptions = {
    variants(card) {
      if (card._variants) return card._variants;
      const el = card.querySelector('[data-card-variants]');
      try { card._variants = JSON.parse(el ? el.textContent : '[]'); } catch (e) { card._variants = []; }
      return card._variants;
    },
    selected(card) {
      return $$('[data-option-index]', card).map((g) => {
        const on = g.querySelector('[data-card-option][aria-pressed="true"]');
        return on ? on.dataset.cardOption : null;
      });
    },
    pick(card, clickedIdx, clickedVal) {
      const variants = this.variants(card);
      const sel = this.selected(card);
      const matchesAll = (v) => v.o.every((o, i) => sel[i] === null || o === sel[i]);
      let v = variants.find((x) => matchesAll(x) && x.a) || variants.find(matchesAll);
      if (!v) {
        v = variants.find((x) => x.o[clickedIdx] === clickedVal && x.a) || variants.find((x) => x.o[clickedIdx] === clickedVal);
        if (v) {
          $$('[data-option-index]', card).forEach((g, i) => {
            $$('[data-card-option]', g).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cardOption === v.o[i])));
          });
        }
      }
      return v;
    },
    apply(card, v) {
      if (!v) return;
      const img = card.querySelector('.card__media img');
      if (img && v.img && img.src !== v.img) {
        img.classList.add('is-swapping');
        const pre = new Image();
        pre.onload = pre.onerror = () => { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = v.img; img.classList.remove('is-swapping'); };
        pre.src = v.img;
      }
      const price = card.querySelector('.card__price .price');
      if (price) {
        const onSale = v.c > v.p;
        let html = `<span class="price__${onSale ? 'sale' : 'regular'}">${formatMoney(v.p)}</span>`;
        if (onSale) html += `<span class="price__compare">${formatMoney(v.c)}</span><span class="price__save">Save ${Math.round(((v.c - v.p) * 100) / v.c)}%</span>`;
        price.innerHTML = html;
        price.classList.toggle('price--on-sale', onSale);
      }
      $$('a[href*="/products/"]', card).forEach((a) => {
        try { const u = new URL(a.getAttribute('href'), window.location.origin); u.searchParams.set('variant', v.id); a.setAttribute('href', u.pathname + u.search); } catch (e) {}
      });
      const idInput = card.querySelector('form [name="id"]');
      if (idInput) idInput.value = v.id;
      const add = card.querySelector('.card__add');
      if (add && add.tagName === 'BUTTON' && !add.classList.contains('is-added')) add.disabled = !v.a;
    }
  };
  document.addEventListener('click', (e) => {
    const more = e.target.closest('[data-card-more]');
    if (more) {
      e.preventDefault();
      $$('.is-hidden', more.parentElement).forEach((b) => b.classList.remove('is-hidden'));
      more.remove();
      return;
    }
    const btn = e.target.closest('[data-card-option]');
    if (!btn) return;
    e.preventDefault();
    const card = btn.closest('[data-product-card]');
    const group = btn.closest('[data-option-index]');
    if (!card || !group) return;
    $$('[data-card-option]', group).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    const v = CardOptions.pick(card, parseInt(group.dataset.optionIndex, 10), btn.dataset.cardOption);
    CardOptions.apply(card, v);
  });

  /* ---------- Shopper memory: recently viewed products + recent searches (localStorage) ---------- */
  const Memory = {
    kProducts: 'pawly_recent_products',
    kSearches: 'pawly_recent_searches',
    read(k) { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch (e) { return []; } },
    write(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
    products() { return this.read(this.kProducts); },
    searches() { return this.read(this.kSearches); },
    rememberProduct() {
      const el = document.querySelector('[data-product-json]');
      if (!el) return;
      try {
        const p = JSON.parse(el.textContent);
        const pick = (v) => (typeof v === 'string' ? v : (v && v.src) || '');
        const image = pick(p.featured_image) || (p.images && p.images.length ? pick(p.images[0]) : '') || (p.media && p.media.length && p.media[0].preview_image ? pick(p.media[0].preview_image.src) : '');
        const item = { id: p.id, handle: p.handle, title: p.title, vendor: p.vendor, url: '/products/' + p.handle, image: image, price: p.price };
        const list = this.products().filter((x) => x.id !== item.id);
        list.unshift(item);
        this.write(this.kProducts, list.slice(0, 12));
      } catch (e) {}
    },
    rememberSearch(term) {
      term = (term || '').trim();
      if (!term || term.length > 60) return;
      const list = this.searches().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      this.write(this.kSearches, list.slice(0, 6));
    },
    init() {
      this.rememberProduct();
      const terms = document.querySelector('[data-search-terms]');
      if (terms) this.rememberSearch(terms.dataset.searchTerms);
      document.addEventListener('submit', (e) => {
        const f = e.target;
        if (f.matches && f.matches('form[role="search"], form[action$="/search"]')) { const i = f.querySelector('input[name="q"]'); if (i) this.rememberSearch(i.value); }
      });
      this.renderPanels();
    },
    imgUrl(src, w) { return typeof src === 'string' && src ? src.replace(/(\.[a-z]{3,4})(\?.*)?$/i, '_' + w + 'x$1$2') : ''; },
    renderPanels() {
      const searches = this.searches();
      $$('[data-recent-searches]').forEach((box) => {
        const list = $('[data-recent-searches-list]', box);
        if (!list || !searches.length) return;
        list.innerHTML = searches.map((t) => '<li><a href="' + T.routes.search + '?q=' + encodeURIComponent(t) + '">' + t.replace(/[<>&]/g, '') + '</a></li>').join('');
        box.classList.remove('hidden');
      });
      const products = this.products();
      $$('[data-recent-products]').forEach((list) => {
        if (!products.length) return;
        list.innerHTML = products.slice(0, 3).map((p) =>
          '<li><a class="search-item" href="' + p.url + '"><span class="search-item__img">' + (p.image ? '<img src="' + this.imgUrl(p.image, 120) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="search-item__body"><span class="search-item__vendor">' + (p.vendor || '') + '</span><span class="search-item__title">' + p.title + '</span></span>' +
          '<span class="search-item__price">' + formatMoney(p.price) + '</span></a></li>').join('');
        list.classList.remove('hidden');
        const defaults = list.parentElement.querySelector('[data-search-defaults]');
        const heading = list.parentElement.querySelector('[data-search-defaults-heading]');
        if (defaults) defaults.classList.add('hidden');
        if (heading) heading.textContent = T.strings.recentlyViewed || 'Recently viewed';
      });
    }
  };

  /* ---------- Search page: "Picked for you" (related to the last viewed product + recently viewed) ---------- */
  const ForYou = {
    async init() {
      const box = document.querySelector('[data-for-you]');
      if (!box) return;
      const products = Memory.products();
      if (!products.length) return;
      const recent = $('[data-for-you-recent]', box), recentGrid = $('[data-for-you-recent-grid]', box);
      if (recent && recentGrid) {
        recentGrid.innerHTML = products.slice(0, 8).map((p) =>
          '<a class="recent-card" href="' + p.url + '"><span class="recent-card__img">' + (p.image ? '<img src="' + Memory.imgUrl(p.image, 300) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="recent-card__title">' + p.title + '</span><span class="recent-card__price">' + formatMoney(p.price) + '</span></a>').join('');
        recent.classList.remove('hidden');
        box.classList.remove('hidden');
      }
      const recs = $('[data-for-you-recs]', box), grid = $('[data-for-you-recs-grid]', box);
      if (!recs || !grid) return;
      try {
        const res = await fetch(box.dataset.recsUrl + '&product_id=' + products[0].id);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-for-you-products]');
        if (fresh && fresh.children.length) { grid.replaceWith(fresh); recs.classList.remove('hidden'); box.classList.remove('hidden'); }
      } catch (e) {}
    }
  };

  /* ---------- Image protection (deterrent: right-click / drag / long-press) ---------- */
  const ImageGuard = {
    init() {
      if (!document.body.classList.contains('protect-images')) return;
      const isMedia = (t) => t && (t.tagName === 'IMG' || t.tagName === 'PICTURE' || t.tagName === 'VIDEO' || t.closest('.gallery__slide, .card__media, .cat-card__img, .collection-banner__img, .popup__media, .hero__media'));
      document.addEventListener('contextmenu', (e) => { if (isMedia(e.target)) e.preventDefault(); });
      document.addEventListener('dragstart', (e) => { if (isMedia(e.target)) e.preventDefault(); });
      document.addEventListener('keydown', (e) => {
        // Ctrl/Cmd+S "save page" and Ctrl/Cmd+U "view source" — casual deterrent only
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) e.preventDefault();
      });
    }
  };

  /* ==========================================================================
     Boot
     ========================================================================== */
  document.addEventListener('DOMContentLoaded', () => {
    Cart.init();
    Header.init();
    Popup.init();
    Wishlist.init();
    ImageGuard.init();
    Memory.init();
    ForYou.init();
  });

  // Theme editor: re-init header behaviours when sections reload
  document.addEventListener('shopify:section:load', () => { Header.initMega(); Header.initLocalization(); Header.initFooterAccordion(); Search.init(); });

  window.Pawly = { Cart, Search, Wishlist, formatMoney, toast, trapFocus, lockScroll };
})();
